import React, { useState, useCallback, useEffect } from 'react';
import { 
  Key, Mail, Webhook as WebhookIcon, ShieldCheck, ShieldAlert, 
  Search, Filter, RefreshCw, Trash2, LayoutDashboard, Activity, Lock, 
  Terminal, Shield, Eye, Plus, ArrowRight, Zap, AlertTriangle, Users, List, X,
  HelpCircle, BookOpen, Info, Clock, Archive, CheckCircle
} from 'lucide-react';
import { showToast } from '../../utils/toast';
import EmailAlertsPanel from '../EmailAlertsPanel';
import WebhookAlertsPanel from '../WebhookAlertsPanel';
import { readJsonErrorResponse } from '../../utils/portalHelpers';

export default function AdminPage({
  apiToken,
  buildRequestHeaders,
  getApiUrl,
  portals,
  adminToken,
  authenticateAdminAccess,
  clientEmail,
  clientName,
  clientNotes,
  clients,
  isAdminAuthenticated,
  isCreatingClient,
  isLoadingClients,
  isUpdatingClient,
  issuedToken,
  loadClients,
  onAdminTokenChange,
  onClientEmailChange,
  onClientNameChange,
  onClientNotesChange,
  onClose,
  toggleClientStatus,
  createClient
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [emailSubscriptions, setEmailSubscriptions] = useState([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [securityRules, setSecurityRules] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [isLoadingSecurity, setIsLoadingSecurity] = useState(false);
  const [isSavingSecurityRule, setIsSavingSecurityRule] = useState(false);
  const [securityRuleType, setSecurityRuleType] = useState('blacklist');
  const [securityIp, setSecurityIp] = useState('');
  const [securityReason, setSecurityReason] = useState('');
  const [apiRequestLogs, setApiRequestLogs] = useState([]);
  const [apiRequestLogTotal, setApiRequestLogTotal] = useState(0);
  const [apiRequestLogSummary, setApiRequestLogSummary] = useState(null);
  const [apiRequestSearch, setApiRequestSearch] = useState('');
  const [apiRequestRoute, setApiRequestRoute] = useState('');
  const [apiRequestStatusCode, setApiRequestStatusCode] = useState('');
  const [isLoadingApiRequests, setIsLoadingApiRequests] = useState(false);
  const [clientControlDrafts, setClientControlDrafts] = useState({});
  const [isSavingClientControl, setIsSavingClientControl] = useState(null);

  // Estados Wayback
  const [isRunningWayback, setIsRunningWayback] = useState(false);
  const [waybackResult, setWaybackResult] = useState(null);
  const [waybackPortalFilter, setWaybackPortalFilter] = useState('');
  const [waybackEngines, setWaybackEngines] = useState(['wayback', 'fuzzing', 'acbr', 'dorks']);

  // Novos estados para filtros de gerenciamento e auditoria
  const [clientFilterStatus, setClientFilterStatus] = useState('active'); // 'active', 'revoked', 'all'
  const [apiRequestTimeWindow, setApiRequestTimeWindow] = useState('1440'); // '60', '1440', '10080', '43200'
  const [apiSelectedKpi, setApiSelectedKpi] = useState(null); // 'successful', 'failed', 'rate_limited', ou null
  const [apiRequestIp, setApiRequestIp] = useState(''); // Filtro de IP exato
  const [apiRequestTokenPrefix, setApiRequestTokenPrefix] = useState(''); // Filtro de Token prefix exato
  const [docSubTab, setDocSubTab] = useState('general'); // Sub-aba da documentação

  const isClientPermanentlyBlocked = (client) => Boolean(client.permanentlyBlockedAt);
  const isClientTemporarilySuspended = (client) => {
    const suspendedUntil = client.suspendedUntil ? new Date(client.suspendedUntil) : null;
    return Boolean(suspendedUntil && !Number.isNaN(suspendedUntil.getTime()) && suspendedUntil > new Date());
  };
  const getClientStatus = (client) => {
    if (isClientPermanentlyBlocked(client)) {
      return {
        label: 'Bloqueio permanente',
        className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border border-rose-200 dark:border-rose-800'
      };
    }

    if (isClientTemporarilySuspended(client)) {
      return {
        label: 'Shadowban Ativo',
        className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800 border-dashed animate-pulse'
      };
    }

    if (!client.isActive) {
      return {
        label: 'Revogado',
        className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
      };
    }

    return {
      label: 'Ativo',
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
    };
  };

  // Filtra chaves de API dinamicamente com base na pílula segmentada selecionada
  const visibleClients = clients.filter((client) => {
    const active = client.isActive && !isClientPermanentlyBlocked(client) && !isClientTemporarilySuspended(client);
    if (clientFilterStatus === 'active') {
      return active;
    }
    if (clientFilterStatus === 'revoked') {
      return !active;
    }
    return true; // 'all'
  });
  
  const activeClients = clients.filter((client) => (
    client.isActive && !isClientPermanentlyBlocked(client) && !isClientTemporarilySuspended(client)
  ));

  const formatSubscriptionDate = (subscription) => {
    const value = subscription?.createdAt || subscription?.created_at;
    const date = value ? new Date(value) : null;

    if (!date || Number.isNaN(date.getTime())) {
      return 'Data indisponível';
    }

    return date.toLocaleDateString('pt-BR');
  };

  const loadEmailSubscriptions = useCallback(async () => {
    if (!isAdminAuthenticated) return;
    
    setIsLoadingSubscriptions(true);
    try {
      const response = await fetch(`${getApiUrl('fiscal-email-subscriptions')}?activeOnly=true`, {
        headers: buildRequestHeaders({ admin: true })
      });
      const payload = await readJsonErrorResponse(response);
      
      if (response.ok) {
        setEmailSubscriptions(payload.subscriptions || []);
      } else {
        throw new Error(payload?.error || 'Falha ao carregar inscrições.');
      }
    } catch (error) {
      console.error('Erro ao carregar inscrições:', error);
      showToast(`Falha ao carregar inscrições: ${error.message}`, { type: 'error' });
    } finally {
      setIsLoadingSubscriptions(false);
    }
  }, [buildRequestHeaders, getApiUrl, isAdminAuthenticated]);

  const loadSecurity = useCallback(async () => {
    if (!isAdminAuthenticated) return;

    setIsLoadingSecurity(true);
    try {
      const response = await fetch(getApiUrl('security'), {
        headers: buildRequestHeaders({ admin: true })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao carregar segurança.');
      }

      setSecurityRules(payload.rules || []);
      setSecurityEvents(payload.events || []);
    } catch (error) {
      showToast(`Falha ao carregar segurança: ${error.message}`, { type: 'error' });
    } finally {
      setIsLoadingSecurity(false);
    }
  }, [buildRequestHeaders, getApiUrl, isAdminAuthenticated]);

  const loadApiRequestLogs = useCallback(async () => {
    if (!isAdminAuthenticated) return;

    setIsLoadingApiRequests(true);
    try {
      const params = new URLSearchParams({ limit: '120', summaryMinutes: apiRequestTimeWindow });
      if (apiRequestRoute) params.set('routeGroup', apiRequestRoute);
      if (apiRequestStatusCode) params.set('statusCode', apiRequestStatusCode);
      if (apiRequestSearch.trim()) params.set('q', apiRequestSearch.trim());
      
      // Filtros aprimorados e KPIs interativos
      if (apiSelectedKpi) params.set('statusType', apiSelectedKpi);
      if (apiRequestIp.trim()) params.set('ip', apiRequestIp.trim());
      if (apiRequestTokenPrefix.trim()) params.set('tokenPrefix', apiRequestTokenPrefix.trim());

      const response = await fetch(`${getApiUrl('api-request-logs')}?${params.toString()}`, {
        headers: buildRequestHeaders({ admin: true })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao carregar consumo da API.');
      }

      setApiRequestLogs(payload.logs || []);
      setApiRequestLogTotal(payload.total || 0);
      setApiRequestLogSummary(payload.summary || null);
    } catch (error) {
      showToast(`Falha ao carregar consumo da API: ${error.message}`, { type: 'error' });
    } finally {
      setIsLoadingApiRequests(false);
    }
  }, [
    apiRequestRoute,
    apiRequestSearch,
    apiRequestStatusCode,
    apiRequestTimeWindow,
    apiSelectedKpi,
    apiRequestIp,
    apiRequestTokenPrefix,
    buildRequestHeaders,
    getApiUrl,
    isAdminAuthenticated
  ]);

  // Load everything when admin is authenticated
  useEffect(() => {
    if (isAdminAuthenticated) {
      loadEmailSubscriptions();
      loadSecurity();
      loadApiRequestLogs();
    }
  }, [isAdminAuthenticated, loadEmailSubscriptions, loadSecurity, loadApiRequestLogs]);

  const TABS = [
    { id: 'overview', label: 'Visão Geral', icon: LayoutDashboard, color: 'text-sky-500' },
    { id: 'api', label: 'API Keys', icon: Key, color: 'text-amber-500' },
    { id: 'email', label: 'E-mails', icon: Mail, color: 'text-emerald-500' },
    { id: 'webhook', label: 'Webhooks', icon: WebhookIcon, color: 'text-indigo-500' },
    { id: 'usage', label: 'Consumo API', icon: Activity, color: 'text-violet-500' },
    { id: 'security', label: 'Segurança', icon: ShieldAlert, color: 'text-rose-500' },
    { id: 'documentation', label: 'Doc Interna', icon: BookOpen, color: 'text-teal-500' },
    { id: 'wayback', label: 'Web Archive', icon: Archive, color: 'text-orange-500' },
  ];

  const createSecurityRule = async () => {
    if (!securityIp.trim()) {
      showToast('Informe um IP, CIDR ou wildcard.', { type: 'warning' });
      return;
    }

    setIsSavingSecurityRule(true);
    try {
      const response = await fetch(getApiUrl('security'), {
        method: 'POST',
        headers: buildRequestHeaders({ admin: true, contentType: 'application/json' }),
        body: JSON.stringify({
          listType: securityRuleType,
          ipCidr: securityIp.trim(),
          reason: securityReason.trim()
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao salvar regra.');
      }

      setSecurityIp('');
      setSecurityReason('');
      setSecurityRules((previous) => [payload.rule, ...previous.filter((rule) => rule.id !== payload.rule.id)]);
      showToast(payload.message || 'Regra salva com sucesso.', { type: 'success' });
    } catch (error) {
      showToast(`Falha ao salvar regra: ${error.message}`, { type: 'error' });
    } finally {
      setIsSavingSecurityRule(false);
    }
  };

  const removeSecurityRule = async (rule) => {
    try {
      const response = await fetch(getApiUrl('security'), {
        method: 'DELETE',
        headers: buildRequestHeaders({ admin: true, contentType: 'application/json' }),
        body: JSON.stringify({ id: rule.id })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao remover regra.');
      }

      setSecurityRules((previous) => previous.filter((item) => item.id !== rule.id));
      showToast(payload.message || 'Regra removida com sucesso.', { type: 'success' });
    } catch (error) {
      showToast(`Falha ao remover regra: ${error.message}`, { type: 'error' });
    }
  };

  const removeEmailSubscription = async (subscription) => {
    if (!window.confirm(`Deseja realmente remover a inscrição de ${subscription.email}?`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl('fiscal-email-subscriptions'), {
        method: 'DELETE',
        headers: buildRequestHeaders({ admin: true, contentType: 'application/json' }),
        body: JSON.stringify({ email: subscription.email })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao remover inscrição.');
      }

      setEmailSubscriptions((previous) => previous.filter((item) => (
        item.id !== subscription.id && item.email !== subscription.email
      )));
      showToast(payload?.message || 'Inscrição removida com sucesso.', { type: 'success' });
    } catch (error) {
      showToast(`Erro ao remover inscrição: ${error.message}`, { type: 'error' });
    }
  };

  const getClientControlValue = (client, field) => {
    const draft = clientControlDrafts[client.id] || {};
    if (Object.prototype.hasOwnProperty.call(draft, field)) {
      return draft[field];
    }

    return client[field] ?? '';
  };

  const updateClientControlDraft = (client, field, value) => {
    setClientControlDrafts((previous) => ({
      ...previous,
      [client.id]: {
        ...(previous[client.id] || {}),
        [field]: value
      }
    }));
  };

  const patchClientControl = async (client, body, actionKey, successMessage) => {
    if (!isAdminAuthenticated) {
      showToast('Libere primeiro o cadastro com o token administrativo.', { type: 'warning' });
      return;
    }

    setIsSavingClientControl(`${client.id}:${actionKey}`);
    try {
      const response = await fetch(getApiUrl('api-clients'), {
        method: 'PATCH',
        headers: buildRequestHeaders({ admin: true, contentType: 'application/json' }),
        body: JSON.stringify({
          id: client.id,
          ...body
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao atualizar o token.');
      }

      setClientControlDrafts((previous) => {
        const next = { ...previous };
        delete next[client.id];
        return next;
      });
      await loadClients();
      showToast(successMessage || payload?.message || 'Token atualizado com sucesso.', { type: 'success' });
    } catch (error) {
      showToast(`Falha ao atualizar token: ${error.message}`, { type: 'error' });
    } finally {
      setIsSavingClientControl(null);
    }
  };

  const saveClientRateLimit = (client) => {
    // Monta a string de escopos a partir dos checkboxes selecionados no rascunho
    const draftScopes = clientControlDrafts[client.id]?.scopesArr;
    let scopesPayload;
    if (draftScopes !== undefined) {
      scopesPayload = [...new Set(['public', ...draftScopes])].join(',');
    } else {
      // Mantém os escopos atuais caso não tenha sido alterado
      scopesPayload = client.scopes || 'public';
    }

    return patchClientControl(client, {
      rateLimitMax: getClientControlValue(client, 'rateLimitMax'),
      rateLimitWindowMs: getClientControlValue(client, 'rateLimitWindowMs'),
      isWhitelisted: Boolean(getClientControlValue(client, 'isWhitelisted')),
      scopes: scopesPayload
    }, 'limits', 'Limites, whitelist e escopos do token atualizados.');
  };

  const applyClientSecurityAction = (client, securityAction) => {
    if (securityAction === 'block_permanent' && !window.confirm(`Bloquear permanentemente o token ${client.name}?`)) {
      return;
    }

    const messages = {
      suspend_24h: 'Token suspenso por 24h.',
      block_permanent: 'Token bloqueado permanentemente.',
      clear_security_block: 'Bloqueio do token removido.'
    };
    const reasons = {
      suspend_24h: 'Suspensão manual de 24h aplicada pelo backoffice',
      block_permanent: 'Bloqueio permanente manual aplicado pelo backoffice',
      clear_security_block: null
    };

    patchClientControl(client, {
      securityAction,
      securityBlockReason: reasons[securityAction]
    }, securityAction, messages[securityAction]);
  };

  const renderAuthSection = () => (
    <div className="flex h-full items-center justify-center p-4 sm:p-6 animate-in zoom-in-95 duration-500">
      <div className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800 sm:p-8">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-300">
            <Lock size={28} />
          </div>

          <span className="mt-5 inline-flex rounded-full bg-gray-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:bg-gray-900 dark:text-gray-400">
            Área administrativa
          </span>
          <h2 className="mt-3 text-2xl font-black tracking-tight text-gray-800 dark:text-white">Acesso ao Backoffice</h2>
          <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">
            Autentique-se para gerenciar infraestrutura, alertas, integrações e segurança do Monitor Fiscal.
          </p>

          <div className="mt-6 w-full space-y-4">
            <label className="block text-left">
              <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Token administrativo
              </span>
              <span className="relative block">
                <Key size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={adminToken}
                  onChange={(event) => onAdminTokenChange(event.target.value)}
                  type="password"
                  placeholder="Insira o token"
                  className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 pl-12 font-mono text-sm text-gray-800 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </span>
            </label>
            <button
              onClick={authenticateAdminAccess}
              disabled={isLoadingClients || !adminToken}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-teal-600 text-xs font-black uppercase tracking-widest text-white shadow-lg shadow-teal-500/20 transition-all hover:bg-teal-700 active:scale-[0.98] disabled:opacity-50"
            >
              {isLoadingClients ? <RefreshCw size={18} className="animate-spin" /> : 'Liberar acesso'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOverviewTab = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-gray-800 dark:text-white tracking-tight">Visão Geral</h2>
          <p className="text-sm text-gray-500">Métricas e atalhos rápidos do seu backoffice.</p>
        </div>
        <button onClick={() => { loadEmailSubscriptions(); loadSecurity(); loadApiRequestLogs(); loadClients(); }} className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-bold rounded-xl transition-colors">
          <RefreshCw size={14} /> Atualizar Tudo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Stat Cards */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm hover:border-amber-300 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Tokens Ativos</h3>
            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 group-hover:bg-amber-100 dark:group-hover:bg-amber-900/40 rounded-xl text-amber-500 transition-colors"><Key size={18} /></div>
          </div>
          <div className="text-3xl font-black text-gray-800 dark:text-white">{activeClients.length}</div>
          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Activity size={12} /> {clients.length} tokens no total</p>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm hover:border-emerald-300 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Inscrições</h3>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/40 rounded-xl text-emerald-500 transition-colors"><Mail size={18} /></div>
          </div>
          <div className="text-3xl font-black text-gray-800 dark:text-white">{emailSubscriptions.length}</div>
          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Users size={12} /> Contatos registrados</p>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm hover:border-indigo-300 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Regras de Seg.</h3>
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/40 rounded-xl text-indigo-500 transition-colors"><Shield size={18} /></div>
          </div>
          <div className="text-3xl font-black text-gray-800 dark:text-white">{securityRules.length}</div>
          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Lock size={12} /> IPs/CIDRs configurados</p>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm hover:border-rose-300 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Alertas</h3>
            <div className="p-2 bg-rose-50 dark:bg-rose-900/20 group-hover:bg-rose-100 dark:group-hover:bg-rose-900/40 rounded-xl text-rose-500 transition-colors"><AlertTriangle size={18} /></div>
          </div>
          <div className="text-3xl font-black text-gray-800 dark:text-white">{securityEvents.filter(e => e.severity === 'warning' || e.severity === 'critical').length}</div>
          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Eye size={12} /> Eventos recentes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ações Rápidas */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
           <h3 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-white mb-5 flex items-center gap-2"><Zap size={18} className="text-amber-500"/> Ações Rápidas</h3>
           <div className="space-y-3">
             <button onClick={() => setActiveTab('api')} className="w-full group flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-amber-50 dark:hover:bg-amber-900/20 border border-transparent hover:border-amber-200 dark:hover:border-amber-800 transition-all text-left">
               <div className="flex items-center gap-4">
                 <div className="p-3 rounded-xl bg-white dark:bg-gray-800 shadow-sm text-amber-500 group-hover:scale-110 transition-transform"><Key size={20} /></div>
                 <div>
                   <div className="text-sm font-bold text-gray-800 dark:text-gray-200">Gerar Nova API Key</div>
                   <div className="text-xs text-gray-500 mt-1">Criar um novo token de acesso para integrações.</div>
                 </div>
               </div>
               <ArrowRight size={18} className="text-gray-400 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
             </button>

             <button onClick={() => setActiveTab('email')} className="w-full group flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border border-transparent hover:border-emerald-200 dark:hover:border-emerald-800 transition-all text-left">
               <div className="flex items-center gap-4">
                 <div className="p-3 rounded-xl bg-white dark:bg-gray-800 shadow-sm text-emerald-500 group-hover:scale-110 transition-transform"><Mail size={20} /></div>
                 <div>
                   <div className="text-sm font-bold text-gray-800 dark:text-gray-200">Nova Inscrição de E-mail</div>
                   <div className="text-xs text-gray-500 mt-1">Cadastrar e-mail para receber notificações.</div>
                 </div>
               </div>
               <ArrowRight size={18} className="text-gray-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
             </button>

             <button onClick={() => setActiveTab('security')} className="w-full group flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800 transition-all text-left">
               <div className="flex items-center gap-4">
                 <div className="p-3 rounded-xl bg-white dark:bg-gray-800 shadow-sm text-indigo-500 group-hover:scale-110 transition-transform"><ShieldCheck size={20} /></div>
                 <div>
                   <div className="text-sm font-bold text-gray-800 dark:text-gray-200">Configurar Firewall / IP</div>
                   <div className="text-xs text-gray-500 mt-1">Adicionar regras de bloqueio ou permissão.</div>
                 </div>
               </div>
               <ArrowRight size={18} className="text-gray-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
             </button>
           </div>
        </div>
        
        {/* Eventos de Segurança */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-white flex items-center gap-2"><Terminal size={18} className="text-gray-500"/> Últimos Eventos</h3>
            <button onClick={() => setActiveTab('security')} className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-white font-bold transition-colors">Ver todos</button>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[350px] space-y-3 custom-scrollbar pr-2">
            {securityEvents.slice(0, 5).map(event => (
              <div key={event.id} className="flex items-start justify-between gap-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full shadow-sm ${event.severity === 'warning' ? 'bg-amber-500 shadow-amber-500/40' : 'bg-sky-500 shadow-sky-500/40'}`} />
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-200">{event.eventType}</span>
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 font-mono bg-white dark:bg-gray-900 px-2 py-1 rounded inline-block border border-gray-100 dark:border-gray-800">{event.ipAddress} • {event.path || '-'}</div>
                </div>
                <div className="text-[10px] font-bold text-gray-400 shrink-0 mt-1">{new Date(event.occurredAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute:'2-digit' })}</div>
              </div>
            ))}
            {securityEvents.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 text-xs py-10 opacity-70">
                <Search size={32} className="mb-3 text-gray-300 dark:text-gray-700" />
                <p>Nenhum evento recente registrado no sistema.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderApiTab = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Formulário de Criação */}
        <div className="w-full lg:w-1/3 space-y-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 rounded-2xl shadow-sm sticky top-0">
          <div className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 pb-4 mb-4">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-500 rounded-lg"><Plus size={18} /></div>
            <div>
              <h3 className="font-black text-gray-800 dark:text-white">Gerar Novo Token</h3>
              <p className="text-[11px] text-gray-500">Credenciais para API externa.</p>
            </div>
          </div>
          
          <div className="grid gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase text-gray-500 mb-1">Nome do Cliente</label>
              <input
                value={clientName}
                onChange={(event) => onClientNameChange(event.target.value)}
                placeholder="Ex: Sistema ERP XPTO"
                className="w-full h-12 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-inner"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase text-gray-500 mb-1">E-mail de Contato</label>
              <input
                value={clientEmail}
                onChange={(event) => onClientEmailChange(event.target.value)}
                placeholder="contato@empresa.com"
                className="w-full h-12 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-inner"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase text-gray-500 mb-1">Observações / Contexto</label>
              <textarea
                value={clientNotes}
                onChange={(event) => onClientNotesChange(event.target.value)}
                placeholder="Para qual finalidade este token será utilizado?"
                className="w-full h-24 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-amber-500 outline-none transition-all resize-none shadow-inner"
              />
            </div>
          </div>

          <button
            onClick={createClient}
            disabled={isCreatingClient}
            className="w-full h-12 rounded-xl bg-amber-500 text-white text-xs font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-amber-500/20 hover:bg-amber-600 active:scale-[0.98] transition-all mt-2"
          >
            {isCreatingClient ? 'Gerando Credencial...' : 'Criar Token de Acesso'}
          </button>

          {issuedToken && (
            <div className="mt-4 rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/20 p-4 space-y-3 animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                  <ShieldCheck size={14} /> Token Gerado
                </span>
              </div>
              <code className="block text-xs break-all text-emerald-800 dark:text-emerald-100 font-mono bg-white dark:bg-gray-950 p-3 rounded-lg border border-emerald-100 dark:border-emerald-800/50 shadow-inner select-all">
                {issuedToken}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(issuedToken);
                  showToast('Copiado com sucesso!', { type: 'success' });
                }}
                className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest transition-colors"
              >
                Copiar para a Área de Transferência
              </button>
            </div>
          )}
        </div>

        {/* Listagem de Ativos */}
        <div className="flex-1 w-full space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 rounded-2xl shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-500"><Filter size={16} /></div>
              <div>
                <h3 className="font-black text-gray-800 dark:text-white">Tokens Gerenciados</h3>
                <p className="text-[11px] text-gray-500">{activeClients.length} ativos, {visibleClients.length} exibidos ({clients.length} no total).</p>
              </div>
            </div>

            {/* Pílulas segmentadas de filtragem de API Keys */}
            <div className="flex p-1 bg-gray-100 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800 shadow-inner text-xs">
              {[
                { id: 'active', label: 'Ativos' },
                { id: 'revoked', label: 'Revogados' },
                { id: 'all', label: 'Todos' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setClientFilterStatus(item.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                    clientFilterStatus === item.id
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'text-gray-500 hover:bg-white dark:hover:bg-gray-900'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button 
              onClick={loadClients} 
              disabled={isLoadingClients}
              className="h-10 px-4 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-bold transition-all disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw size={14} className={isLoadingClients ? 'animate-spin' : ''} />
              Sincronizar Lista
            </button>
          </div>

          {visibleClients.length > 0 ? (
            <div className="grid gap-3">
              {visibleClients.map((client) => {
                const status = getClientStatus(client);
                const suspendedUntil = client.suspendedUntil ? new Date(client.suspendedUntil) : null;

                return (
                <div key={client.id} className="group rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 hover:border-amber-300 dark:hover:border-amber-700 transition-all shadow-sm">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="text-base font-black text-gray-800 dark:text-white truncate">{client.name}</div>
                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${status.className}`}>{status.label}</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3">
                        <span className="flex items-center gap-1.5"><Mail size={12} /> {client.email || 'Sem e-mail'}</span>
                        <span className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 font-mono"><Key size={12} /> {client.tokenPrefix}••••••••</span>
                      </div>
                      {client.suspensionCount > 0 && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">
                          <Clock size={11} /> Suspensões acumuladas: {client.suspensionCount} ({client.suspensionCount * 5 >= 1440 ? 'Teto de 24h atingido' : `${client.suspensionCount * 5} min`})
                        </div>
                      )}
                      {(client.permanentlyBlockedAt || isClientTemporarilySuspended(client)) && (
                        <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-[11px] font-bold text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
                          {client.permanentlyBlockedAt
                            ? `Bloqueado permanentemente em ${new Date(client.permanentlyBlockedAt).toLocaleString('pt-BR')}. Contato: me@mateusemth.dev`
                            : `Sob shadowban ativo até ${suspendedUntil?.toLocaleString('pt-BR') || 'a próxima janela de liberação'} (Punição nº ${client.suspensionCount || 0}).`}
                          {client.securityBlockReason ? ` Motivo: ${client.securityBlockReason}.` : ''}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => toggleClientStatus(client)}
                      disabled={isUpdatingClient === client.id}
                      className={`shrink-0 h-10 px-4 rounded-xl text-xs font-black uppercase tracking-widest border transition-all disabled:opacity-50 flex items-center gap-2 ${
                        client.isActive
                          ? 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-900/20 dark:border-rose-900/40 hover:bg-rose-100 dark:hover:bg-rose-900/60'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-900/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60'
                      }`}
                    >
                      {isUpdatingClient === client.id ? <RefreshCw size={14} className="animate-spin" /> : client.isActive ? <Trash2 size={14} /> : <ShieldCheck size={14} />}
                      {client.isActive ? 'Revogar' : 'Ativar'}
                    </button>
                  </div>
                  
                  {client.notes && (
                    <div className="mt-4 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-950 p-3 rounded-xl border border-gray-100 dark:border-gray-800/80 italic">
                      "{client.notes}"
                    </div>
                  )}

                  <div className="mt-4 grid grid-cols-1 gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950/50 xl:grid-cols-[1fr_1fr_1fr_auto] items-center">
                    <label className="block">
                      <span className="mb-1 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-gray-400">
                        Limite por janela
                        <span className="relative group/tooltip inline-block cursor-help">
                          <HelpCircle size={10} className="text-gray-400 hover:text-amber-500 transition-colors" />
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-gray-900 p-3.5 text-[10px] font-medium leading-relaxed text-white opacity-0 transition-opacity group-hover/tooltip:opacity-100 shadow-xl z-50 text-left normal-case tracking-normal">
                            <strong className="block text-amber-400 font-bold mb-1">Limite Máximo de Requisições</strong>
                            Define o total de requisições que esta chave pode fazer dentro do período configurado na janela. Ao esgotar, o cliente recebe um erro <span className="font-mono text-amber-300 font-black">429 (Too Many Requests)</span>.
                            <span className="block mt-2 pt-2 border-t border-gray-800 text-gray-400">
                              <strong className="text-gray-300 font-bold">Padrão Global:</strong> 120 requisições
                            </span>
                            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
                          </span>
                        </span>
                      </span>
                      <input
                        type="number"
                        min="1"
                        value={getClientControlValue(client, 'rateLimitMax')}
                        onChange={(event) => updateClientControlDraft(client, 'rateLimitMax', event.target.value)}
                        placeholder="Global"
                        className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 font-mono text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-amber-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-gray-400">
                        Janela em ms
                        <span className="relative group/tooltip inline-block cursor-help">
                          <HelpCircle size={10} className="text-gray-400 hover:text-amber-500 transition-colors" />
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 rounded-lg bg-gray-900 p-3.5 text-[10px] font-medium leading-relaxed text-white opacity-0 transition-opacity group-hover/tooltip:opacity-100 shadow-xl z-50 text-left normal-case tracking-normal">
                            <strong className="block text-amber-400 font-bold mb-1">Janela de Tempo (Milissegundos)</strong>
                            Período de tempo para redefinir o limite de consumo.
                            <span className="block mt-2 pt-2 border-t border-gray-800 text-gray-400">
                              <strong className="text-gray-300 font-bold">Padrão Global:</strong> 60.000 ms (1 minuto)
                            </span>
                            <span className="block mt-2 font-semibold text-gray-300">Valores de referência comuns:</span>
                            <span className="block pl-1.5 mt-0.5">• <span className="font-mono text-amber-300 font-black">60000</span> = 1 Minuto</span>
                            <span className="block pl-1.5">• <span className="font-mono text-amber-300 font-black">300000</span> = 5 Minutos</span>
                            <span className="block pl-1.5">• <span className="font-mono text-amber-300 font-black">3600000</span> = 1 Hora</span>
                            <span className="block pl-1.5">• <span className="font-mono text-amber-300 font-black">86400000</span> = 24 Horas</span>
                            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
                          </span>
                        </span>
                      </span>
                      <input
                        type="number"
                        min="1000"
                        step="1000"
                        value={getClientControlValue(client, 'rateLimitWindowMs')}
                        onChange={(event) => updateClientControlDraft(client, 'rateLimitWindowMs', event.target.value)}
                        placeholder="Global"
                        className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 font-mono text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-amber-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                      />
                    </label>
                    
                    {/* Toggle de Bypass de Rate Limit (Whitelist) */}
                    <div className="flex items-center h-full pt-4">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={Boolean(getClientControlValue(client, 'isWhitelisted'))}
                          onChange={(event) => updateClientControlDraft(client, 'isWhitelisted', event.target.checked)}
                          className="h-4.5 w-4.5 rounded border-gray-300 text-amber-500 focus:ring-amber-500 dark:border-gray-700 dark:bg-gray-900"
                        />
                        <div>
                          <span className="block text-[9px] font-black uppercase tracking-widest text-gray-400">Bypass Rate Limit</span>
                          <span className="text-[9px] text-gray-400 font-medium">Isenção de cota (Passagem infinita)</span>
                        </div>
                      </label>
                    </div>

                    {/* Seção de Escopos (Scopes) da Chave de API */}
                    <div className="col-span-full mt-1">
                      <span className="mb-2 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-gray-400">
                        <ShieldCheck size={10} />
                        Escopos de Acesso
                        <span className="relative group/tooltip inline-block cursor-help">
                          <HelpCircle size={10} className="text-gray-400 hover:text-amber-500 transition-colors" />
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 rounded-lg bg-gray-900 p-3.5 text-[10px] font-medium leading-relaxed text-white opacity-0 transition-opacity group-hover/tooltip:opacity-100 shadow-xl z-50 text-left normal-case tracking-normal">
                            <strong className="block text-amber-400 font-bold mb-1">Escopos (Scopes) da API Key</strong>
                            Definem quais grupos de endpoints esta chave pode consumir. E-mail e notificações liberam somente suas integrações; Admin delega operações administrativas.
                            <span className="block mt-2 pt-2 border-t border-gray-800 text-gray-400"><strong className="text-gray-300">Public</strong> é sempre obrigatório e não pode ser removido.</span>
                            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
                          </span>
                        </span>
                      </span>
                      <div className="flex flex-wrap gap-3 mt-1">
                        {[
                          { id: 'public', label: 'Public', desc: 'Rotas públicas (obrigatório)', color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800', locked: true },
                          { id: 'email', label: 'Email', desc: 'Disparo de e-mails', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800', locked: false },
                          { id: 'notifications', label: 'Notifications', desc: 'Webhooks e alertas', color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800', locked: false },
                          { id: 'admin', label: 'Admin', desc: 'Operações administrativas delegáveis', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800', locked: false },
                        ].map((scope) => {
                          // Verifica os escopos atuais do rascunho ou do cliente
                          const currentScopesStr = clientControlDrafts[client.id]?.scopesArr !== undefined
                            ? clientControlDrafts[client.id].scopesArr
                            : String(client.scopes || 'public').split(',').map(s => s.trim()).filter(Boolean);
                          const isChecked = scope.locked || currentScopesStr.includes(scope.id);

                          return (
                            <label
                              key={scope.id}
                              className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer select-none transition-all ${
                                isChecked ? scope.bg : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-60'
                              } ${scope.locked ? 'cursor-not-allowed' : 'hover:opacity-100'}`}
                            >
                              <input
                                type="checkbox"
                                disabled={scope.locked}
                                checked={isChecked}
                                onChange={(e) => {
                                  const current = String(client.scopes || 'public').split(',').map(s => s.trim()).filter(Boolean);
                                  const draftArr = clientControlDrafts[client.id]?.scopesArr ?? current;
                                  let updated;
                                  if (e.target.checked) {
                                    updated = [...new Set([...draftArr, scope.id])];
                                  } else {
                                    updated = draftArr.filter(s => s !== scope.id);
                                  }
                                  // Garante que 'public' sempre está presente
                                  if (!updated.includes('public')) updated.unshift('public');
                                  setClientControlDrafts(prev => ({
                                    ...prev,
                                    [client.id]: { ...(prev[client.id] || {}), scopesArr: updated }
                                  }));
                                }}
                                className="h-3.5 w-3.5 rounded border-gray-300 accent-amber-500 disabled:opacity-50"
                              />
                              <div>
                                <span className={`block text-[9px] font-black uppercase tracking-widest ${scope.color}`}>{scope.label}</span>
                                <span className="text-[9px] text-gray-400 font-medium">{scope.desc}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <button
                      onClick={() => saveClientRateLimit(client)}
                      disabled={isSavingClientControl === `${client.id}:limits`}
                      className="flex h-10 items-center justify-center gap-2 self-end rounded-lg bg-amber-500 px-4 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-amber-600 disabled:opacity-50"
                    >
                      {isSavingClientControl === `${client.id}:limits` ? <RefreshCw size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                      Salvar
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => applyClientSecurityAction(client, 'suspend_24h')}
                      disabled={isSavingClientControl === `${client.id}:suspend_24h`}
                      className="h-9 rounded-lg border border-amber-200 bg-amber-50 px-3 text-[10px] font-black uppercase tracking-widest text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300"
                    >
                      Suspender 24h
                    </button>
                    <button
                      onClick={() => applyClientSecurityAction(client, 'block_permanent')}
                      disabled={isSavingClientControl === `${client.id}:block_permanent`}
                      className="h-9 rounded-lg border border-rose-200 bg-rose-50 px-3 text-[10px] font-black uppercase tracking-widest text-rose-700 transition-colors hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300"
                    >
                      Bloquear permanente
                    </button>
                    <button
                      onClick={() => applyClientSecurityAction(client, 'clear_security_block')}
                      disabled={isSavingClientControl === `${client.id}:clear_security_block`}
                      className="h-9 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-[10px] font-black uppercase tracking-widest text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-300"
                    >
                      Liberar bloqueio
                    </button>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-[10px] text-gray-400 font-medium">
                    <span>Gerado em: {new Date(client.createdAt).toLocaleDateString('pt-BR')}</span>
                    <span className="flex items-center gap-1">
                      <Activity size={10} /> Último acesso: {client.lastUsedAt ? new Date(client.lastUsedAt).toLocaleString('pt-BR') : 'nunca utilizado'}
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-2xl bg-gray-50 dark:bg-gray-900/50">
              <Search size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm font-bold text-gray-500">Nenhum token cadastrado no momento.</p>
              <p className="text-xs text-gray-400 mt-1">Crie um novo token utilizando o formulário lateral.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEmailTab = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl text-emerald-500"><Mail size={24} /></div>
          <div>
            <h2 className="text-xl font-black text-gray-800 dark:text-white tracking-tight">Assinaturas de E-mail</h2>
            <p className="text-sm text-gray-500">Gerencie a lista de contatos que recebem alertas do sistema.</p>
          </div>
        </div>
        <button 
          onClick={loadEmailSubscriptions} 
          disabled={isLoadingSubscriptions}
          className="h-11 px-5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50 flex items-center gap-2 border border-emerald-200 dark:border-emerald-800/50"
        >
          <RefreshCw size={14} className={isLoadingSubscriptions ? 'animate-spin' : ''} />
          Sincronizar
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        <div className="w-full lg:w-1/3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 rounded-2xl shadow-sm sticky top-0">
           <h3 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-white mb-4 border-b border-gray-100 dark:border-gray-800 pb-3 flex items-center gap-2">
             <Plus size={16} className="text-emerald-500" /> Cadastrar Novo E-mail
           </h3>
           <div className="mt-4">
            <EmailAlertsPanel
              isTab
              apiToken={apiToken}
              buildRequestHeaders={buildRequestHeaders}
              getApiUrl={getApiUrl}
              isAdminAuthenticated={isAdminAuthenticated}
              onClose={() => {
                showToast('Inscrição finalizada.', { type: 'success' });
                loadEmailSubscriptions();
              }}
              portals={portals}
            />
           </div>
        </div>

        <div className="flex-1 w-full space-y-3">
          {isLoadingSubscriptions ? (
            <div className="p-16 text-center bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
              <RefreshCw size={32} className="mx-auto text-emerald-500 animate-spin mb-4" />
              <p className="text-sm text-gray-500 font-bold uppercase tracking-widest">Carregando lista de inscrições...</p>
            </div>
          ) : emailSubscriptions.length > 0 ? (
            <div className="grid gap-3">
              {emailSubscriptions.map((sub) => (
                <div key={sub.id} className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-black text-gray-800 dark:text-white truncate">{sub.name || 'Contato Sem Nome'}</div>
                      <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium truncate mt-1 bg-emerald-50 dark:bg-emerald-900/10 inline-block px-2 py-0.5 rounded border border-emerald-100 dark:border-emerald-800/30">{sub.email}</div>
                    </div>
                    <button
                      className="shrink-0 h-10 px-4 flex items-center gap-2 rounded-xl text-xs font-black uppercase tracking-widest text-gray-500 hover:text-rose-600 bg-gray-50 hover:bg-rose-50 dark:bg-gray-800 dark:hover:bg-rose-900/30 border border-gray-200 dark:border-gray-700 hover:border-rose-200 dark:hover:border-rose-800/50 transition-all"
                      onClick={() => removeEmailSubscription(sub)}
                    >
                      <Trash2 size={14} /> Remover
                    </button>
                  </div>
                  
                  <div className="mt-4 flex flex-wrap gap-2">
                    {sub.topics?.map((topic) => (
                      <span key={topic.id} className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-tight shadow-sm">
                        {topic.id.replace(':', ' - ')}
                      </span>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-[10px] text-gray-400 font-medium flex items-center gap-1.5">
                    <Activity size={12} /> Cadastrado em: {formatSubscriptionDate(sub)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-2xl bg-white dark:bg-gray-900/50">
              <Mail size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm font-bold text-gray-500">Nenhum e-mail inscrito.</p>
              <p className="text-xs text-gray-400 mt-1">Utilize o painel lateral para adicionar contatos.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderWebhookTab = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5 rounded-2xl shadow-sm">
        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl text-indigo-500"><WebhookIcon size={24} /></div>
        <div>
          <h2 className="text-xl font-black text-gray-800 dark:text-white tracking-tight">Gerenciamento de Webhooks</h2>
          <p className="text-sm text-gray-500">Conecte o sistema a plataformas como Discord, Slack ou endpoints genéricos.</p>
        </div>
      </div>
      
      <div className="bg-transparent">
        <WebhookAlertsPanel
          apiToken={apiToken}
          buildRequestHeaders={buildRequestHeaders}
          getApiUrl={getApiUrl}
          isAdminAuthenticated={isAdminAuthenticated}
          portals={portals}
        />
      </div>
    </div>
  );

  const formatLogDate = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return 'Data indisponível';
    return date.toLocaleString('pt-BR');
  };

  const renderApiUsageTab = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5 rounded-2xl shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-violet-50 dark:bg-violet-900/20 rounded-xl text-violet-600 dark:text-violet-300">
            <Activity size={24} />
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-800 dark:text-white tracking-tight">Consumo da API</h2>
            <p className="text-sm text-gray-500">Requests reais, status HTTP, token usado e janela de rate limit.</p>
          </div>
        </div>
        
        <div className="flex items-center flex-wrap gap-3">
          {/* Seletor Temporal (Janela de tempo dos KPIs) */}
          <div className="flex p-1 bg-gray-100 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800 shadow-inner text-xs">
            {[
              { id: '60', label: '1h' },
              { id: '1440', label: '24h' },
              { id: '10080', label: '7d' },
              { id: '43200', label: '30d' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setApiRequestTimeWindow(item.id)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                  apiRequestTimeWindow === item.id
                    ? 'bg-violet-600 text-white shadow-md'
                    : 'text-gray-500 hover:bg-white dark:hover:bg-gray-900'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button
            onClick={loadApiRequestLogs}
            disabled={isLoadingApiRequests}
            className="h-11 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-violet-500/20"
          >
            <RefreshCw size={14} className={isLoadingApiRequests ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>

      {/* KPIs de logs interativos */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {[
          { id: 'all', label: 'Requests', value: apiRequestLogSummary?.total ?? 0, color: 'hover:border-violet-400 dark:hover:border-violet-600', activeBg: 'border-violet-500 bg-violet-50/50 dark:bg-violet-950/20 ring-2 ring-violet-500/20' },
          { id: 'successful', label: 'Sucesso', value: apiRequestLogSummary?.successful ?? 0, color: 'hover:border-emerald-400 dark:hover:border-emerald-600', activeBg: 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 ring-2 ring-emerald-500/20' },
          { id: 'failed', label: 'Falhas', value: apiRequestLogSummary?.failed ?? 0, color: 'hover:border-rose-400 dark:hover:border-rose-600', activeBg: 'border-rose-500 bg-rose-50/50 dark:bg-rose-950/20 ring-2 ring-rose-500/20' },
          { id: 'rate_limited', label: 'Rate limit', value: apiRequestLogSummary?.rateLimited ?? 0, color: 'hover:border-amber-400 dark:hover:border-amber-600', activeBg: 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20 ring-2 ring-amber-500/20' },
          { id: 'tokens', label: 'Tokens', value: apiRequestLogSummary?.tokens ?? 0, color: 'hover:border-indigo-400 dark:hover:border-indigo-600', activeBg: '', isCountOnly: true },
          { id: 'ips', label: 'IPs', value: apiRequestLogSummary?.ips ?? 0, color: 'hover:border-cyan-400 dark:hover:border-cyan-600', activeBg: '', isCountOnly: true }
        ].map((item) => {
          const isActive = apiSelectedKpi === item.id || (item.id === 'all' && !apiSelectedKpi);
          const cursorStyle = item.isCountOnly ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-all';
          return (
            <button
              key={item.label}
              disabled={item.isCountOnly}
              onClick={() => {
                if (item.id === 'all') {
                  setApiSelectedKpi(null);
                } else {
                  setApiSelectedKpi(apiSelectedKpi === item.id ? null : item.id);
                }
              }}
              className={`text-left rounded-2xl border p-4 shadow-sm select-none ${cursorStyle} ${
                isActive && !item.isCountOnly
                  ? item.activeBg
                  : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 ' + item.color
              }`}
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">{item.label}</p>
              <p className="mt-2 text-2xl font-black text-gray-800 dark:text-white">{item.value}</p>
            </button>
          );
        })}
      </div>

      {/* Barra de filtros de logs aprimorada */}
      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 lg:grid-cols-[180px_110px_160px_160px_1fr_auto]">
        <label className="block">
          <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">Rota</span>
          <select
            value={apiRequestRoute}
            onChange={(event) => setApiRequestRoute(event.target.value)}
            className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-violet-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
          >
            <option value="">Todas</option>
            <option value="/api/fiscal-status">/api/fiscal-status</option>
            <option value="/api/fiscal-documents">/api/fiscal-documents</option>
            <option value="/api/fiscal-api">/api/fiscal-api</option>
            <option value="/api/bot-heartbeat">/api/bot-heartbeat</option>
            <option value="/api/fiscal-proxy">/api/fiscal-proxy</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">Status</span>
          <input
            value={apiRequestStatusCode}
            onChange={(event) => setApiRequestStatusCode(event.target.value.replace(/\D/g, '').slice(0, 3))}
            placeholder="200, 429..."
            className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-violet-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">Filtro IP</span>
          <input
            value={apiRequestIp}
            onChange={(event) => setApiRequestIp(event.target.value)}
            placeholder="IP exato (ex: 127.0.0.1)"
            className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-violet-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">Prefixo Token</span>
          <input
            value={apiRequestTokenPrefix}
            onChange={(event) => setApiRequestTokenPrefix(event.target.value)}
            placeholder="Prefixo exato (12 ch.)"
            className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-violet-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">Busca Global</span>
          <input
            value={apiRequestSearch}
            onChange={(event) => setApiRequestSearch(event.target.value)}
            placeholder="IP, origem, rota, etc."
            className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-violet-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
          />
        </label>
        <div className="flex items-end">
          <button
            onClick={loadApiRequestLogs}
            className="h-11 w-full rounded-xl bg-gray-800 px-4 text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-gray-900 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 lg:w-auto"
          >
            Filtrar
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-800">
          <h3 className="text-xs font-black uppercase tracking-widest text-gray-800 dark:text-white flex items-center gap-2">
            <Terminal size={16} className="text-violet-500" /> Requests capturados
          </h3>
          <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold text-gray-500 dark:bg-gray-800 dark:text-gray-300">
            {apiRequestLogs.length} de {apiRequestLogTotal}
          </span>
        </div>

        <div className="max-h-[70vh] overflow-y-auto bg-gray-50 p-4 dark:bg-gray-950/20 custom-scrollbar">
          {isLoadingApiRequests ? (
            <div className="py-16 text-center">
              <RefreshCw size={30} className="mx-auto animate-spin text-violet-500" />
              <p className="mt-3 text-xs font-black uppercase tracking-widest text-gray-500">Carregando consumo da API...</p>
            </div>
          ) : apiRequestLogs.length > 0 ? (
            <div className="space-y-3">
              {apiRequestLogs.map((log) => (
                <div key={log.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${log.statusCode >= 500 ? 'bg-rose-500' : log.statusCode >= 400 ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                          {log.method}
                        </span>
                        <span className="font-mono text-sm font-black text-gray-800 dark:text-white">{log.routeGroup || log.path}</span>
                        <span className={`rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                          log.statusCode >= 400
                            ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300'
                            : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                        }`}>
                          HTTP {log.statusCode || '-'}
                        </span>
                      </div>
                      <div className="mt-2 truncate rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 font-mono text-[11px] text-gray-600 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-300" title={log.path}>
                        {log.path}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                        <span className="font-mono">{formatLogDate(log.occurredAt)}</span>
                        {log.durationMs !== null && log.durationMs !== undefined ? <span className="font-mono">{log.durationMs}ms</span> : null}
                        {log.tokenPrefix ? <span className="font-mono">token {log.tokenPrefix}...</span> : <span>sem token</span>}
                        {log.apiClientId ? <span>cliente #{log.apiClientId}</span> : null}
                        {log.ipAddress ? <span>{log.ipAddress}</span> : null}
                        {log.origin ? <span className="truncate">{log.origin}</span> : null}
                      </div>
                    </div>
                    <div className="grid shrink-0 grid-cols-2 gap-2 text-[10px] xl:w-56">
                      <div className="rounded-lg bg-gray-50 p-2 dark:bg-gray-950">
                        <p className="font-black uppercase tracking-widest text-gray-400">Limite</p>
                        <p className="mt-1 font-mono font-black text-gray-700 dark:text-gray-200">{log.rateLimitLimit ?? '-'}</p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-2 dark:bg-gray-950">
                        <p className="font-black uppercase tracking-widest text-gray-400">Restante</p>
                        <p className="mt-1 font-mono font-black text-gray-700 dark:text-gray-200">{log.rateLimitRemaining ?? '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Search size={36} className="mx-auto text-gray-300 dark:text-gray-700" />
              <p className="mt-3 text-sm font-bold text-gray-500">Nenhum consumo registrado ainda.</p>
              <p className="mt-1 text-xs text-gray-400">A partir do deploy, cada request real da API passa a ser gravado aqui.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-rose-50 dark:bg-rose-900/20 rounded-xl text-rose-500"><ShieldAlert size={24} /></div>
          <div>
            <h2 className="text-xl font-black text-gray-800 dark:text-white tracking-tight">Central de Segurança</h2>
            <p className="text-sm text-gray-500">Monitore acessos, bloqueie IPs suspeitos e configure regras de firewall.</p>
          </div>
        </div>
        <button
          onClick={loadSecurity}
          disabled={isLoadingSecurity}
          className="h-11 px-5 rounded-xl bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50 flex items-center gap-2 border border-rose-200 dark:border-rose-800/50"
        >
          <RefreshCw size={14} className={isLoadingSecurity ? 'animate-spin' : ''} />
          Sincronizar
        </button>
      </div>

      <div className="flex flex-col xl:flex-row gap-8 items-start">
        {/* Controle de IP */}
        <div className="w-full xl:w-5/12 space-y-6">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-white mb-5 border-b border-gray-100 dark:border-gray-800 pb-3 flex items-center gap-2">
              <Lock size={16} className="text-rose-500" /> Nova Regra de IP / Firewall
            </h3>
            
            <div className="space-y-4">
              <div className="flex p-1 bg-gray-100 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800 shadow-inner">
                {[
                  { id: 'blacklist', label: 'Bloquear (Blacklist)', activeClass: 'bg-rose-500 text-white shadow-md', icon: ShieldAlert },
                  { id: 'whitelist', label: 'Permitir (Whitelist)', activeClass: 'bg-emerald-500 text-white shadow-md', icon: ShieldCheck }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSecurityRuleType(item.id)}
                    className={`flex-1 h-10 flex items-center justify-center gap-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                      securityRuleType === item.id
                        ? item.activeClass
                        : 'text-gray-500 hover:bg-white dark:hover:bg-gray-900'
                    }`}
                  >
                    <item.icon size={14} /> {item.label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase text-gray-500 mb-1">Endereço IP ou Rede (CIDR)</label>
                <input
                  value={securityIp}
                  onChange={(event) => setSecurityIp(event.target.value)}
                  placeholder="Ex: 192.168.1.1 ou 10.0.0.0/24"
                  className="w-full h-12 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-mono focus:ring-2 focus:ring-rose-500 outline-none transition-all shadow-inner"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-gray-500 mb-1">Motivo / Descrição</label>
                <input
                  value={securityReason}
                  onChange={(event) => setSecurityReason(event.target.value)}
                  placeholder="Por que esta regra foi criada?"
                  className="w-full h-12 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-rose-500 outline-none transition-all shadow-inner"
                />
              </div>

              <button
                onClick={createSecurityRule}
                disabled={isSavingSecurityRule}
                className="w-full h-12 rounded-xl bg-gray-800 dark:bg-white text-white dark:text-gray-900 text-xs font-black uppercase tracking-widest disabled:opacity-50 shadow-lg hover:bg-gray-900 dark:hover:bg-gray-100 active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-2"
              >
                {isSavingSecurityRule ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
                Adicionar Regra
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden flex flex-col max-h-[500px]">
            <div className="p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/50">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-800 dark:text-white flex items-center gap-2">
                <List size={14} className="text-gray-400" /> Regras Ativas ({securityRules.length})
              </h3>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {securityRules.length > 0 ? (
                securityRules.map((rule) => (
                  <div key={rule.id} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm hover:border-rose-300 transition-colors group">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${
                            rule.listType === 'allowlist' || rule.listType === 'whitelist'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                              : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800'
                          }`}>
                            {rule.listType === 'allowlist' || rule.listType === 'whitelist' ? 'Permitido' : 'Bloqueado'}
                          </span>
                          <code className="text-sm font-black text-gray-800 dark:text-white bg-gray-100 dark:bg-gray-950 px-2 py-0.5 rounded">{rule.ipCidr}</code>
                        </div>
                        {rule.reason && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-3">"{rule.reason}"</p>
                        )}
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-medium">
                          <Activity size={10} /> Criado em {new Date(rule.createdAt).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                      <button
                        onClick={() => removeSecurityRule(rule)}
                        className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-all border border-transparent hover:border-rose-200 dark:hover:border-rose-800/50"
                        title="Remover regra"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-10 text-center text-gray-400 text-xs">
                  <ShieldCheck size={24} className="mx-auto mb-2 text-gray-300 dark:text-gray-700" />
                  Nenhuma regra de acesso configurada.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Registro de Eventos */}
        <div className="w-full xl:w-7/12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm flex flex-col h-[800px] max-h-[80vh]">
          <div className="p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/50 flex items-center justify-between">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-800 dark:text-white flex items-center gap-2">
              <Terminal size={16} className="text-gray-500" /> Log de Eventos de Segurança
            </h3>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-200 dark:bg-gray-800 px-2 py-1 rounded-full">{securityEvents.length} registros</span>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar bg-gray-50 dark:bg-gray-950/20">
            {securityEvents.length > 0 ? (
              securityEvents.map((event) => (
                <div key={event.id} className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 p-4 shadow-sm relative overflow-hidden group">
                  {/* Decorative severity line */}
                  <div className={`absolute top-0 left-0 w-1 h-full ${event.severity === 'warning' ? 'bg-amber-500' : event.severity === 'critical' ? 'bg-rose-500' : 'bg-sky-500'}`} />
                  
                  <div className="pl-2">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                          event.severity === 'critical' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400' : 
                          event.severity === 'warning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' : 
                          'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400'
                        }`}>
                          {event.eventType}
                        </span>
                        <code className="text-xs font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-950 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-800">
                          {event.ipAddress || 'IP Oculto'}
                        </code>
                      </div>
                      <span className="text-[10px] font-bold text-gray-400 font-mono">
                        {new Date(event.occurredAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50 p-2.5 rounded-lg border border-gray-100 dark:border-gray-800/80">
                      <div className="flex items-center gap-1.5 truncate" title={`Rota: ${event.method || '-'} ${event.path || '-'}`}>
                        <span className="font-bold text-gray-400 uppercase">Rota:</span> 
                        <span className="font-mono text-gray-700 dark:text-gray-300">{event.method || '-'} {event.path || '-'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-gray-400 uppercase">Status:</span> 
                        <span className={`font-black ${event.statusCode >= 400 ? 'text-rose-500' : 'text-emerald-500'}`}>{event.statusCode || '-'}</span>
                      </div>
                      {event.origin && (
                        <div className="flex items-center gap-1.5 sm:col-span-2 truncate text-gray-500" title={event.origin}>
                          <span className="font-bold text-gray-400 uppercase">Origem:</span> {event.origin}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Search size={40} className="mx-auto text-gray-300 dark:text-gray-700 mb-4" />
                <p className="text-sm font-bold text-gray-500">Log limpo.</p>
                <p className="text-xs text-gray-400 mt-1">Nenhum evento suspeito registrado recentemente.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocumentationTab = () => {
    const subtabs = [
      { id: 'general', label: 'Visão Geral', icon: Shield, desc: 'Arquitetura e Firewall' },
      { id: 'scopes', label: 'Escopos da API', icon: Key, desc: 'Permissões delegáveis' },
      { id: 'infractions', label: 'Infrações (24h)', icon: AlertTriangle, desc: 'Contagem e Gatilhos' },
      { id: 'suspensions', label: 'Shadowban & Bloqueios', icon: Clock, desc: 'Progressivo e Educativo' },
      { id: 'lists', label: 'Listas de Acesso', icon: List, desc: 'Blacklist e Whitelist' },
      { id: 'localhost', label: 'Bypass Localhost', icon: Terminal, desc: 'Ambiente de Dev' },
    ];

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 text-gray-800 dark:text-gray-100">
        {/* Cabeçalho Premium com Degradê */}
        <div className="relative overflow-hidden rounded-3xl border border-teal-100 bg-gradient-to-r from-teal-500/10 to-emerald-500/5 p-6 shadow-sm dark:border-teal-900/30">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-500 text-white shadow-md shadow-teal-500/20 dark:bg-teal-600">
                <BookOpen size={24} />
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-800 dark:text-white tracking-tight">Documentação Interna de Segurança</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Entenda os mecanismos de proteção, rate limits e regras de suspensão do Monitor Fiscal.</p>
              </div>
            </div>
            <div className="hidden shrink-0 items-center gap-1.5 rounded-full bg-teal-100/50 px-3 py-1 text-xs font-bold text-teal-800 dark:bg-teal-950/50 dark:text-teal-300 sm:flex">
              <ShieldCheck size={14} />
              <span>Manual Oficial do Firewall</span>
            </div>
          </div>
        </div>

        {/* Layout de duas colunas: Navegação Secundária e Conteúdo */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Navegação da Documentação */}
          <aside className="w-full shrink-0 space-y-2 lg:w-64">
            <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-950">
              <p className="px-3 py-2 text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">Tópicos da Guia</p>
              <nav className="space-y-1">
                {subtabs.map((sub) => {
                  const isSubActive = docSubTab === sub.id;
                  return (
                    <button
                      key={sub.id}
                      onClick={() => setDocSubTab(sub.id)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
                        isSubActive
                          ? 'bg-teal-50 text-teal-950 font-bold dark:bg-teal-900/30 dark:text-teal-50 border-l-4 border-teal-500 shadow-sm'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-900/50 dark:hover:text-gray-200 border-l-4 border-transparent'
                      }`}
                    >
                      <sub.icon size={16} className={`shrink-0 ${isSubActive ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <div className="min-w-0">
                        <p className="text-xs truncate">{sub.label}</p>
                        <p className="text-[9px] text-gray-400 dark:text-gray-500 truncate font-normal">{sub.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Conteúdo Dinâmico da Documentação */}
          <section className="flex-1 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-950 min-w-0">
            {docSubTab === 'general' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3 dark:border-gray-800">
                  <Shield className="text-teal-600 dark:text-teal-400" size={20} />
                  <h3 className="text-lg font-black text-gray-800 dark:text-white">Arquitetura Geral de Segurança</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  O ecossistema do <strong>Monitor Fiscal</strong> utiliza um gateway de segurança inteligente de dupla camada que gerencia restrições ao nível de rede (endereço de IP) e de aplicação (chaves de API).
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-900/30">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400 mb-3">
                      <List size={18} />
                    </div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">1. Camada de Rede (IP)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      Verifica e filtra conexões com base no IP de origem utilizando Blacklists (lista de restrição total) e Whitelists de IP confiáveis, bloqueando acessos suspeitos sumariamente no início do ciclo HTTP.
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-900/30">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 mb-3">
                      <Key size={18} />
                    </div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">2. Camada de Aplicação (API Key)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      Valida chaves de API, limites de consumo (Rate Limit), origens CORS associadas, caminhos de arquivo requisitados e aplica punições automáticas (suspensões) caso infrações de segurança sejam detectadas.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-amber-200/60 bg-amber-50/30 p-4 dark:border-amber-900/30 dark:bg-amber-950/20">
                  <div className="flex gap-3">
                    <Info className="shrink-0 text-amber-600 dark:text-amber-500 mt-0.5" size={16} />
                    <div>
                      <h5 className="text-xs font-bold text-amber-800 dark:text-amber-400">Automatização de Punições</h5>
                      <p className="text-xs text-amber-700 dark:text-amber-400/90 mt-1 leading-relaxed">
                        O firewall foi desenhado para ser auto-gerenciável. Ele calcula infrações em tempo real, pune usuários abusivos com suspensão imediata e os reabilita após o prazo de forma automática, eliminando a sobrecarga de suporte do administrador.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {docSubTab === 'scopes' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3 dark:border-gray-800">
                  <Key className="text-teal-600 dark:text-teal-400" size={20} />
                  <h3 className="text-lg font-black text-gray-800 dark:text-white">Escopos e Autorização Delegada</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  API Keys externas usam <code>Authorization: Bearer ef_live_...</code>. A chave começa com
                  <strong> public</strong> e recebe permissões adicionais somente quando você as libera na aba API Keys.
                </p>
                <div className="grid gap-3">
                  {[
                    ['public', 'Documentos fiscais e fiscal-notifications', 'Sempre presente'],
                    ['email', 'Tópicos, inscrições, listagem e envio de e-mails', 'Delegável'],
                    ['notifications', 'Webhooks, alertas e notificações internas', 'Delegável'],
                    ['admin', 'Clientes, logs, segurança, heartbeat e rotinas administrativas', 'Delegação crítica']
                  ].map(([scope, access, status]) => (
                    <div key={scope} className="grid gap-2 rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-900/30 md:grid-cols-[130px_1fr_140px] md:items-center">
                      <code className="text-xs font-black text-teal-700 dark:text-teal-300">{scope}</code>
                      <span className="text-xs text-gray-600 dark:text-gray-300">{access}</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{status}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 text-xs text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/20 dark:text-rose-300">
                  O escopo <strong>admin</strong> permite criar e alterar outras API Keys. Conceda-o somente a integrações totalmente confiáveis.
                  Proxy, contas, status, comparações e downloads continuam exclusivos de <code>API_APPLICATION_TOKEN</code> ou <code>x-admin-token</code>.
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Consultar permissões da própria chave</p>
                  <code className="mt-2 block text-xs text-gray-700 dark:text-gray-200">GET /api/fiscal-api-keys/scopes</code>
                </div>
              </div>
            )}

            {docSubTab === 'infractions' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3 dark:border-gray-800">
                  <AlertTriangle className="text-amber-500" size={20} />
                  <h3 className="text-lg font-black text-gray-800 dark:text-white">O Fluxo de Infrações (Janela de 24 horas)</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  Para detectar e desencorajar abusos antes de aplicar um bloqueio definitivo, o sistema monitora infrações em uma janela móvel de tempo.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800/80">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                    <div className="p-3 border-r border-gray-250 dark:border-gray-800">
                      <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Janela Móvel de Monitoramento</p>
                      <p className="text-2xl font-black text-gray-700 dark:text-gray-200 mt-1">24 Horas</p>
                      <p className="text-[10px] text-gray-400 mt-1">Parâmetro: API_CLIENT_INFRACTION_WINDOW_HOURS</p>
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Teto Máximo de Tolerância</p>
                      <p className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">10 Infrações</p>
                      <p className="text-[10px] text-gray-400 mt-1">Parâmetro: API_CLIENT_INFRACTION_THRESHOLD</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-black uppercase tracking-widest text-gray-400">Gatilhos que Computam Infrações</h4>
                  
                  <div className="space-y-2">
                    <div className="flex gap-3 rounded-xl border border-gray-100 p-3.5 dark:border-gray-800/60 hover:bg-gray-50/30 dark:hover:bg-gray-900/10 transition-colors">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-[10px] font-bold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">1</span>
                      <div>
                        <code className="text-xs font-bold text-gray-850 dark:text-gray-200">rate_limited</code>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          Quando o cliente ultrapassa a cota de requisições por janela configurada e insiste em fazer chamadas contínuas recebendo erros de taxa excedida (HTTP 429).
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 rounded-xl border border-gray-100 p-3.5 dark:border-gray-800/60 hover:bg-gray-50/30 dark:hover:bg-gray-900/10 transition-colors">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-[10px] font-bold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">2</span>
                      <div>
                        <code className="text-xs font-bold text-gray-850 dark:text-gray-200">cors_origin_denied</code>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          Requisições disparadas via navegadores por sites de terceiros ou origens HTTP que não foram devidamente autorizados nos cabeçalhos CORS vinculados a esta chave de API.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 rounded-xl border border-gray-100 p-3.5 dark:border-gray-800/60 hover:bg-gray-50/30 dark:hover:bg-gray-900/10 transition-colors">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-[10px] font-bold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">3</span>
                      <div>
                        <code className="text-xs font-bold text-gray-850 dark:text-gray-200">suspicious_path</code>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          Tentativas de carregar caminhos de arquivos suspeitos, não existentes, ou áreas restritas (como `.env`, `/wp-admin`, `.git/config` ou arquivos de configurações do sistema).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {docSubTab === 'suspensions' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3 dark:border-gray-800">
                  <Clock className="text-teal-600 dark:text-teal-400" size={20} />
                  <h3 className="text-lg font-black text-gray-800 dark:text-white">Shadowban Educativo & Punição Progressiva</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  Para promover o consumo saudável da API e evitar que falhas de integração derrubem sistemas, o <strong>Monitor Fiscal</strong> utiliza a política de <strong>Shadowban Progressivo e Educativo</strong>. Quando um cliente excede o teto móvel de 10 infrações nas últimas 24 horas, o firewall ativa suspensões automáticas cujo tempo cresce de forma gradual e didática:
                </p>

                <div className="space-y-4">
                  {/* Etapa 1: Punição Inicial */}
                  <div className="rounded-xl border border-amber-200/80 bg-amber-50/10 p-5 dark:border-amber-950/50 dark:bg-amber-950/10">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 font-bold text-xs">1</div>
                        <h4 className="font-bold text-amber-800 dark:text-amber-400 text-sm">Infração Inicial: Shadowban de 5 Minutos</h4>
                      </div>
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-850 dark:bg-amber-950 dark:text-amber-300 text-[10px] font-black uppercase tracking-wider">Automática</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      A chave de API é suspensa temporariamente por apenas <strong>5 minutos</strong>. É um tempo educativo curto, focado em alertar desenvolvedores sobre loops de erros ou estouros de cota. O servidor rejeita as chamadas com erro <code className="text-[10px] bg-amber-100 dark:bg-amber-950 px-1 py-0.5 rounded text-amber-750 dark:text-amber-300 font-mono">403 Forbidden (api_token_suspended)</code>.
                    </p>
                  </div>

                  {/* Etapa 2: Acúmulo Progressivo */}
                  <div className="rounded-xl border border-teal-200/80 bg-teal-50/10 p-5 dark:border-teal-950/50 dark:bg-teal-950/10">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-150 text-teal-700 dark:bg-teal-950 dark:text-teal-400 font-bold text-xs">2</div>
                        <h4 className="font-bold text-teal-850 dark:text-teal-400 text-sm">Reincidência Linear: Somando +5 Minutos por ciclo</h4>
                      </div>
                      <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-850 dark:bg-teal-950 dark:text-teal-355 text-[10px] font-black uppercase tracking-wider">Linear Acumulativo</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      Caso o cliente seja liberado e volte a estourar o limite de infrações, o sistema calcula uma punição acumulada linear: <strong>(Nº de suspensões × 5 minutos)</strong>. 
                    </p>
                    <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-[10px] font-bold text-gray-500">
                      <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-150 dark:border-gray-800">1ª Suspensão<br/><span className="text-teal-600 dark:text-teal-400 font-black text-xs">5 Minutos</span></div>
                      <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-150 dark:border-gray-800">2ª Suspensão<br/><span className="text-teal-600 dark:text-teal-400 font-black text-xs">10 Minutos</span></div>
                      <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-150 dark:border-gray-800">3ª Suspensão<br/><span className="text-teal-600 dark:text-teal-400 font-black text-xs">15 Minutos</span></div>
                      <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-150 dark:border-gray-800">... até o Teto máximo<br/><span className="text-rose-600 dark:text-rose-400 font-black text-xs">24 Horas</span></div>
                    </div>
                  </div>

                  {/* Etapa 3: Softblock */}
                  <div className="rounded-xl border border-orange-200/80 bg-orange-50/10 p-5 dark:border-orange-950/50 dark:bg-orange-950/10">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400 font-bold text-xs">3</div>
                        <h4 className="font-bold text-orange-850 dark:text-orange-400 text-sm">Recorrência Crítica: Softblock de 48 horas</h4>
                      </div>
                      <span className="px-2 py-0.5 rounded bg-orange-150 text-orange-800 dark:bg-orange-950 dark:text-orange-300 text-[10px] font-black uppercase tracking-wider">Janela de 48h</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      Se a punição acumulada anterior do cliente já atingiu o teto máximo de 24 horas (ou se o ban recente foi de 24h) e o cliente reincidir dentro da <strong>janela de reincidência de 48 horas</strong> após ser reabilitado, a punição passa a ser um <strong>Softblock de 48 horas</strong> completo.
                    </p>
                  </div>

                  {/* Etapa 4: Permablock */}
                  <div className="rounded-xl border border-rose-200/80 bg-rose-50/10 p-5 dark:border-rose-950/50 dark:bg-rose-950/10">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400 font-bold text-xs">4</div>
                        <h4 className="font-bold text-rose-800 dark:text-rose-400 text-sm">Reincidência pós-Softblock: Bloqueio Permanente (Permablock)</h4>
                      </div>
                      <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-850 dark:bg-rose-950 dark:text-rose-350 text-[10px] font-black uppercase tracking-wider">Manual Release</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      Se mesmo após o Softblock de 48 horas o cliente voltar a falhar e cometer 10 infrações dentro de nova janela de reincidência curta de 48h, a chave de API é <strong>bloqueada de forma permanente</strong> (<code className="text-[10px] bg-rose-100 dark:bg-rose-950 px-1 py-0.5 rounded text-rose-700 dark:text-rose-300 font-mono">api_token_permanently_blocked</code>). O desbloqueio exige que o administrador reative a chave manualmente no Backoffice, o que também zera a contagem de suspensões acumuladas de forma educativa.
                    </p>
                  </div>
                </div>

                {/* Transparência de Tempo Restante */}
                <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-900/30 text-xs text-gray-500 leading-relaxed space-y-2">
                  <p className="font-bold text-gray-650 dark:text-gray-300 flex items-center gap-1.5">
                    <Zap size={14} className="text-amber-500 animate-pulse" />
                    Transparência Total & Integração Amigável (retryAfterMs):
                  </p>
                  <p>
                    Para facilitar a automação por parte dos clientes da API, toda requisição negada com status 403 Forbidden por suspensão temporária retorna um payload JSON estruturado contendo a propriedade <code className="text-[10px] bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300 font-mono font-bold">retryAfterMs</code> (e <code className="text-[10px] bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300 font-mono font-bold">retryAfterSeconds</code>). Isso permite que os robôs parceiros leiam o tempo exato restante em milissegundos e aguardem a reabilitação automática sem sobrecarregar o firewall.
                  </p>
                </div>
              </div>
            )}

            {docSubTab === 'lists' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3 dark:border-gray-800">
                  <List className="text-indigo-500" size={20} />
                  <h3 className="text-lg font-black text-gray-800 dark:text-white">Listas de Acesso: Blacklist & Whitelist</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  Gerencie restrições e privilégios específicos para controlar fluxos de requisição por IPs e chaves de API individuais.
                </p>

                <div className="space-y-4">
                  {/* Blacklist de IP */}
                  <div className="rounded-xl border border-gray-100 bg-gray-50/30 p-4 dark:border-gray-800 dark:bg-gray-900/10">
                    <h4 className="font-bold text-gray-800 dark:text-white text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-rose-500" />
                      1. Blacklist (Blocklist de IP)
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      Funciona a nível de infraestrutura/rede. Cadastre IPs individuais ou blocos de sub-rede CIDR suspeitos (ex: <code className="text-[10px] bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded font-mono">192.168.1.1</code> ou <code className="text-[10px] bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded font-mono">203.0.113.0/24</code>) para rejeição imediata com erro <code className="text-[10px] bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded font-mono">403 Forbidden (ip_blocked)</code>.
                    </p>
                  </div>

                  {/* Whitelist de IP */}
                  <div className="rounded-xl border border-gray-100 bg-gray-50/30 p-4 dark:border-gray-800 dark:bg-gray-900/10">
                    <h4 className="font-bold text-gray-800 dark:text-white text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-sky-500" />
                      2. Whitelist de IP (Allowlist)
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      Concede imunidade a determinados endereços de IP conhecidos e seguros, garantindo que eles nunca sejam acidentalmente bloqueados pelas verificações de firewall da Blacklist.
                    </p>
                  </div>

                  {/* Whitelist de Token */}
                  <div className="rounded-xl border border-teal-200/80 bg-teal-50/10 p-4 dark:border-teal-950/50 dark:bg-teal-950/10">
                    <h4 className="font-bold text-teal-800 dark:text-teal-400 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-teal-500 animate-pulse" />
                      3. Bypass de Rate Limit (Whitelist de Chave de API)
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      Ao habilitar o botão <strong>"Bypass Rate Limit (Whitelist)"</strong> em uma chave de API na aba de gerenciamento, essa chave é marcada com <code className="text-[10px] bg-teal-100 dark:bg-teal-950 px-1 py-0.5 rounded text-teal-700 dark:text-teal-300">is_whitelisted = TRUE</code>.
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
                      A chave ganha <strong>passagem infinita e irrestrita</strong>: o sistema ignora o controle de Rate Limit no Redis/Memory e nunca aplica bloqueios por volume de requisições. As chamadas retornam cabeçalhos com o indicador de cota ilimitada (<code className="text-[10px] bg-teal-100 dark:bg-teal-950 px-1 py-0.5 rounded text-teal-700 dark:text-teal-300">X-RateLimit-Limit: unlimited</code>).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {docSubTab === 'localhost' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3 dark:border-gray-800">
                  <Terminal className="text-teal-600 dark:text-teal-400" size={20} />
                  <h3 className="text-lg font-black text-gray-800 dark:text-white">Bypass Completo de Localhost</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  Para dar a melhor experiência de desenvolvimento, acelerar testes integrados e evitar que o próprio desenvolvedor se auto-bloqueie em sua máquina, criamos um mecanismo de <strong>bypass completo para localhost</strong>.
                </p>

                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-900/30">
                  <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Isenções Ativas em Conexões de Localhost</h4>
                  <ul className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 dark:text-teal-400 font-bold shrink-0">✓</span>
                      <span><strong>Bypass de CORS:</strong> A API responde o cabeçalho CORS correspondente exato à origem (ex: portais locais rodando em portas como 3000), garantindo acesso perfeito no browser sem erros de requisição bloqueada pelo navegador.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 dark:text-teal-400 font-bold shrink-0">✓</span>
                      <span><strong>Bypass de IP:</strong> A máquina local está imune e nunca é consultada contra regras de bloqueio ativo na Blacklist.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 dark:text-teal-400 font-bold shrink-0">✓</span>
                      <span><strong>Bypass de Suspensão/Bloqueio:</strong> Se você estiver simulando testes locais e sua chave for suspensa temporariamente ou bloqueada por reincidência, a validação interna ignora as colunas de punição se a chamada for detectada localmente, permitindo prosseguir sem travamentos.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 dark:text-teal-400 font-bold shrink-0">✓</span>
                      <span><strong>Sem Infrações Acidentais:</strong> Executar múltiplos scripts locais concorrentes não computará logs de infração com pontuação punitiva no banco de dados.</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-xl border border-teal-200/50 bg-teal-50/20 p-4 dark:border-teal-950/30 dark:bg-teal-950/20 text-xs text-teal-850 dark:text-teal-400 leading-relaxed">
                  <p className="font-bold">🔎 Transparência Total de Tráfego:</p>
                  <p className="mt-1">
                    Embora todos os bloqueios e restrições estejam desativados localmente para facilidade de desenvolvimento, os logs de requisições continuam sendo gravados e salvos no banco. Você poderá ver e analisar suas chamadas locais na aba de <strong>Consumo API</strong> em tempo real!
                  </p>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  };

  const runWaybackImport = useCallback(async () => {
    if (!isAdminAuthenticated) return;

    setIsRunningWayback(true);
    setWaybackResult(null);
    try {
      const response = await fetch(getApiUrl('wayback-import'), {
        method: 'POST',
        headers: buildRequestHeaders({ admin: true, contentType: 'application/json' }),
        body: JSON.stringify({ portalFilter: waybackPortalFilter || null, engines: waybackEngines })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao rodar o importador do Web Archive.');
      }

      showToast('Importação de arquivos antigos finalizada com sucesso!', { type: 'success' });
      setWaybackResult(payload);
    } catch (error) {
      showToast(`Erro na importação: ${error.message}`, { type: 'error' });
    } finally {
      setIsRunningWayback(false);
    }
  }, [buildRequestHeaders, getApiUrl, isAdminAuthenticated, waybackPortalFilter, waybackEngines]);

  const renderWaybackTab = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-gray-800 dark:text-white tracking-tight flex items-center gap-2">
            <Archive size={24} className="text-orange-500" /> Web Archive
          </h2>
          <p className="text-sm text-gray-500">Importação de documentos antigos via Wayback Machine (API CDX).</p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-black text-gray-800 dark:text-white mb-2">Sincronizar Arquivos da SVRS</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Isso fará uma varredura completa na API CDX do Wayback Machine procurando por arquivos não indexados no portal atual da SVRS. Todos os links que baterem com a rota `downloadarquivoestatico` serão processados. Apenas documentos novos serão salvos no banco.
        </p>

        <div className="mb-6 max-w-sm">
          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Filtrar por Documento/Portal</label>
          <select
            value={waybackPortalFilter}
            onChange={(e) => setWaybackPortalFilter(e.target.value)}
            disabled={isRunningWayback}
            className="w-full h-12 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-orange-500 outline-none transition-all shadow-inner"
          >
            <option value="">Todos (Varredura Completa)</option>
            {Object.entries(portals).map(([id, portal]) => (
              <option key={id} value={id}>{portal.label}</option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3">Motores de Busca</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            {[
              { id: 'wayback', label: 'Wayback Machine', desc: 'Pesquisa no histórico do Web Archive' },
              { id: 'fuzzing', label: 'Fuzzing de Padrões', desc: 'Tenta variações lógicas de nomes na SEFAZ' },
              { id: 'acbr', label: 'Repositórios (ACBr/NFePHP)', desc: 'Consulta repositórios de projetos fiscais' },
              { id: 'dorks', label: 'Search Engine Dorks', desc: 'Procura resultados perdidos no Google/DuckDuckGo' }
            ].map(engine => (
              <label key={engine.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 cursor-pointer hover:border-orange-300 transition-colors">
                <input 
                  type="checkbox" 
                  className="mt-0.5 w-4 h-4 text-orange-500 rounded border-gray-300 focus:ring-orange-500"
                  checked={waybackEngines.includes(engine.id)}
                  onChange={(e) => {
                    if (e.target.checked) setWaybackEngines(prev => [...prev, engine.id]);
                    else setWaybackEngines(prev => prev.filter(id => id !== engine.id));
                  }}
                  disabled={isRunningWayback}
                />
                <div>
                  <div className="text-xs font-bold text-gray-800 dark:text-gray-200">{engine.label}</div>
                  <div className="text-[10px] text-gray-500 leading-tight mt-0.5">{engine.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={runWaybackImport}
          disabled={isRunningWayback}
          className="h-12 px-6 rounded-xl bg-orange-500 text-white text-xs font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-orange-500/20 hover:bg-orange-600 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          {isRunningWayback ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <Archive size={16} />
          )}
          {isRunningWayback ? 'Buscando arquivos...' : 'Iniciar Importação do Wayback Machine'}
        </button>

        {waybackResult && (
          <div className="mt-6 space-y-4 animate-in fade-in slide-in-from-top-2">
            <div className="rounded-xl border border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800 p-5">
              <h4 className="text-sm font-black text-orange-800 dark:text-orange-300 mb-3 flex items-center gap-2">
                <CheckCircle size={16} /> Resultado da Importação
              </h4>
              <ul className="text-sm space-y-2 text-orange-900 dark:text-orange-100">
                <li><strong>Total de URLs Encontradas (Wayback):</strong> {waybackResult.totalUrlsFound}</li>
                <li><strong>Documentos Inseridos:</strong> {waybackResult.inserted}</li>
                <li><strong>Ignorados (Já existiam/Inválidos):</strong> {waybackResult.skipped}</li>
                <li><strong>Erros:</strong> {waybackResult.errors}</li>
              </ul>
            </div>

            {/* Listagem detalhada */}
            {waybackResult.details && (
              <div className="space-y-3">
                {waybackResult.details.inserted?.length > 0 && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-4">
                    <h5 className="text-xs font-black uppercase tracking-widest text-emerald-800 dark:text-emerald-300 mb-3">Inseridos com Sucesso</h5>
                    <div className="max-h-48 overflow-y-auto space-y-2 custom-scrollbar pr-2">
                      {waybackResult.details.inserted.map((doc, idx) => (
                        <div key={idx} className="bg-white dark:bg-emerald-950/50 p-2 rounded border border-emerald-100 dark:border-emerald-800/50 text-xs flex flex-wrap items-center gap-2">
                          <strong className="text-emerald-700 dark:text-emerald-400">{doc.nome || 'Sem Nome'}</strong>
                          <span className="text-gray-500">({doc.portalName || doc.portalSlug})</span>
                          {doc.recovered_via && (
                            <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded text-[9px] font-black uppercase tracking-widest">
                              {doc.recovered_via}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {waybackResult.details.skipped?.length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 dark:bg-gray-800/40 dark:border-gray-700/50 p-4">
                    <h5 className="text-xs font-black uppercase tracking-widest text-gray-600 dark:text-gray-300 mb-3">Ignorados</h5>
                    <div className="max-h-48 overflow-y-auto space-y-2 custom-scrollbar pr-2">
                      {waybackResult.details.skipped.map((doc, idx) => (
                        <div key={idx} className="bg-white dark:bg-gray-900/50 p-2 rounded border border-gray-100 dark:border-gray-700/50 text-[11px]">
                          <strong className="text-gray-700 dark:text-gray-200">{doc.nome || doc.url || 'Desconhecido'}</strong>
                          <span className="text-gray-400 ml-2 italic">- Motivo: {doc.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {waybackResult.details.errors?.length > 0 && (
                  <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4">
                    <h5 className="text-xs font-black uppercase tracking-widest text-red-800 dark:text-red-300 mb-3">Erros Encontrados</h5>
                    <div className="max-h-48 overflow-y-auto space-y-2 custom-scrollbar pr-2">
                      {waybackResult.details.errors.map((doc, idx) => (
                        <div key={idx} className="bg-white dark:bg-red-950/50 p-2 rounded border border-red-100 dark:border-red-800/50 text-[11px]">
                          <strong className="text-red-700 dark:text-red-400">{doc.nome || doc.url || 'Desconhecido'}</strong>
                          <span className="text-red-500 ml-2">- {doc.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-gray-100 p-3 text-gray-900 font-sans animate-in fade-in zoom-in-95 duration-300 dark:bg-gray-900 dark:text-gray-100 sm:p-4 lg:p-8">
      <div className="mx-auto flex h-full min-h-0 w-full max-w-[112rem] flex-col overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
        <header className="flex-none border-b border-gray-100 bg-white px-4 py-4 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-300">
                <LayoutDashboard size={20} />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-base font-black uppercase tracking-tight text-gray-800 dark:text-white">Backoffice Administrativo</h1>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${isAdminAuthenticated
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    }`}>
                    {isAdminAuthenticated ? 'Autenticado' : 'Acesso restrito'}
                  </span>
                </div>
                <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                  {isAdminAuthenticated ? 'Monitoramento em tempo real, tokens de acesso, alertas e firewall.' : 'Autenticação requerida para gerenciar o backoffice.'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 text-xs font-black uppercase tracking-widest text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-700"
              title="Fechar Backoffice"
            >
              <X size={16} />
              Voltar ao App
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 lg:flex-row">
          {isAdminAuthenticated && (
            <aside className="shrink-0 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:w-72 lg:border-b-0 lg:border-r">
              <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-700 lg:px-5 lg:py-5">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
                    <ShieldCheck size={17} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-black uppercase tracking-widest text-gray-800 dark:text-white">Sistema seguro</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">Operação administrativa</p>
                  </div>
                </div>
              </div>

              <nav className="flex gap-2 overflow-x-auto p-3 lg:flex-col lg:overflow-y-auto lg:px-3 lg:py-4">
                {TABS.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`group relative flex h-11 shrink-0 items-center gap-3 rounded-xl border px-4 text-xs font-bold transition-all lg:w-full ${
                        isActive
                          ? 'border-teal-200 bg-teal-50 text-teal-800 shadow-sm dark:border-teal-900/50 dark:bg-teal-900/20 dark:text-teal-100'
                          : 'border-transparent text-gray-500 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-700 dark:hover:bg-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      <tab.icon size={18} className={`shrink-0 ${isActive ? 'text-teal-600 dark:text-teal-300' : tab.color}`} />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>
          )}

          <main className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
            {!isAdminAuthenticated ? (
              <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
                {renderAuthSection()}
              </div>
            ) : (
              <div className="mx-auto min-h-full max-w-7xl p-4 sm:p-6 lg:p-8">
                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'api' && renderApiTab()}
                {activeTab === 'email' && renderEmailTab()}
                {activeTab === 'webhook' && renderWebhookTab()}
                {activeTab === 'usage' && renderApiUsageTab()}
                {activeTab === 'security' && renderSecurityTab()}
                {activeTab === 'documentation' && renderDocumentationTab()}
                {activeTab === 'wayback' && renderWaybackTab()}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
